import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { CATEGORIES } from "@/data/catalog";
import { createBulkInquiry } from "@/lib/api";

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-yellow";

const BulkInquiry = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    category: "",
    quantity: "",
    deadline: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSending(true);
    try {
      await createBulkInquiry(form);
      setSent(true);
      toast.success("Inquiry received — we'll hit you back within 24h");
    } catch {
      toast.error("Could not send inquiry. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="bulk-inquiry-success" className="halftone border border-brand-yellow/40 bg-ink-3 p-10 text-center">
        <p className="font-display text-3xl font-black uppercase text-brand-yellow">Locked In</p>
        <p className="mt-2 text-sm text-zinc-400">
          Your bulk inquiry is in the queue. Our production team replies within one business day.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="bulk-inquiry-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <input
        data-testid="bulk-inquiry-name"
        value={form.name}
        onChange={set("name")}
        placeholder="Your name *"
        className={inputCls}
      />
      <input
        data-testid="bulk-inquiry-email"
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder="Email *"
        className={inputCls}
      />
      <input
        data-testid="bulk-inquiry-company"
        value={form.company}
        onChange={set("company")}
        placeholder="Company / Organization"
        className={inputCls}
      />
      <select
        data-testid="bulk-inquiry-category"
        value={form.category}
        onChange={set("category")}
        className={inputCls}
      >
        <option value="">Select category</option>
        {CATEGORIES.map((c) => (
          <option key={c.slug} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>
      <input
        data-testid="bulk-inquiry-quantity"
        value={form.quantity}
        onChange={set("quantity")}
        placeholder="Estimated quantity (e.g. 250)"
        className={inputCls}
      />
      <input
        data-testid="bulk-inquiry-deadline"
        type="date"
        value={form.deadline}
        onChange={set("deadline")}
        className={`${inputCls} [color-scheme:dark]`}
      />
      <textarea
        data-testid="bulk-inquiry-message"
        value={form.message}
        onChange={set("message")}
        placeholder="Tell us about the job — sizes, colors, artwork status…"
        rows={4}
        className={`${inputCls} sm:col-span-2`}
      />
      <button
        data-testid="bulk-inquiry-submit"
        type="submit"
        disabled={sending}
        className="flex items-center justify-center gap-2 bg-brand-yellow py-3.5 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-magenta hover:text-white disabled:opacity-50 sm:col-span-2"
      >
        <Send className="h-4 w-4" />
        {sending ? "Sending…" : "Request Bulk Quote"}
      </button>
    </form>
  );
};

export default BulkInquiry;
